import React from 'react';
import { motion } from 'framer-motion';
import { AppStoreButton, GooglePlayButton } from '../common/StoreButtons';
import MealMarquee from './MealMarquee';

const HeroSection: React.FC = () => {
    return (
        <section
            id="top"
            className="relative z-[1] scroll-mt-24 px-5 pt-[clamp(118px,15vh,168px)]"
        >
            <div className="mx-auto grid max-w-[1180px] grid-cols-1 items-center gap-[clamp(28px,4vw,64px)] lg:grid-cols-2">
                {/* Left: copy */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                    className="min-w-0"
                >
                    <div className="inline-flex items-center gap-[9px] rounded-full border border-dark-gunmetal bg-dark-gunmetal py-1.5 pl-2 pr-3.5">
                        <span className="inline-flex h-[22px] w-[22px] items-center justify-center rounded-full bg-[#E2FFEE]">
                            <svg
                                width="13"
                                height="13"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#1A1F25"
                                strokeWidth="1.8"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                aria-hidden="true"
                            >
                                <path d="M12 3l1.6 5.4a2 2 0 0 0 1.3 1.3L20 11l-5.1 1.3a2 2 0 0 0-1.3 1.3L12 19l-1.6-5.4a2 2 0 0 0-1.3-1.3L4 11l5.1-1.3a2 2 0 0 0 1.3-1.3z" />
                            </svg>
                        </span>
                        <span className="text-[10px] font-medium uppercase tracking-[0.14em] text-dark-textSecondary">
                            Photo-first nutrition intelligence
                        </span>
                    </div>

                    <h1 className="mt-[26px] max-w-[13ch] text-[clamp(38px,5.2vw,76px)] font-extrabold leading-[0.98] tracking-[-0.04em] text-white">
                        Nutrition clarity from{' '}
                        <span className="bg-hero-shine bg-clip-text text-transparent">
                            a single photo
                        </span>
                    </h1>

                    <p className="mt-6 max-w-[44ch] text-[17px] leading-relaxed text-dark-textSecondary [text-wrap:pretty]">
                        Fork turns everyday meal photos into macros, health scores, and coaching
                        prompts so you can make better choices without tedious logging.
                    </p>

                    <div className="mt-8 flex flex-wrap gap-3">
                        <AppStoreButton />
                        <GooglePlayButton />
                    </div>
                </motion.div>

                {/* Right: app preview video */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.96 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1], delay: 0.15 }}
                    className="relative flex min-w-0 justify-center px-0 pb-1 pt-4"
                >
                    {/* wrapper owns the centring so the scale animation can't clobber it */}
                    <div
                        aria-hidden="true"
                        className="pointer-events-none absolute left-1/2 top-1/2 aspect-square w-[min(440px,100%)] -translate-x-1/2 -translate-y-1/2"
                    >
                        <div className="h-full w-full animate-breathe-hero rounded-full bg-hero-glow blur-[26px]" />
                    </div>

                    <div className="relative w-[min(288px,78vw)]">
                        <div className="box-border block w-[288px] max-w-[82vw] rounded-[44px] bg-phone-frame p-[9px] shadow-phone">
                            <div className="relative h-[584px] w-[270px] max-w-full overflow-hidden rounded-[36px] bg-dark-base">
                                <span
                                    aria-hidden="true"
                                    className="absolute left-1/2 top-2 z-[9] -ml-[42px] block h-6 w-[84px] rounded-full bg-black"
                                />
                                {/* preload="metadata" keeps the 3MB demo off the
                                    critical path; the poster fills the frame
                                    until playback starts. */}
                                <video
                                    src="/assets/app-demo.mp4"
                                    poster="/assets/app-demo-poster.webp"
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                    preload="metadata"
                                    aria-label="Fork app demo"
                                    className="block h-full w-full object-cover"
                                />
                            </div>
                        </div>
                    </div>
                </motion.div>
            </div>

            <MealMarquee />
        </section>
    );
};

export default HeroSection;
